import * as React from 'react';
import {
  IRatingsInputProps,
  IRatingsInputImperativeActions,
} from '../RatingsInput.types';
import RatingsInputItem from './RatingsInputItem/RatingsInputItem';
import { classes, st } from './RatingsInput.component.st.css';

const fiveItems = [1, 2, 3, 4, 5];

const Title = React.memo<{ title: string }>(({ title }) => (
  <span className={classes.title} data-hook="rating-input-title">
    {title}
  </span>
));

const HiddenLabels = React.memo<{ labels: { [key: number]: string } }>(
  ({ labels }) => (
    <>
      {fiveItems.map(value => {
        return (
          <span key={value} className={classes.label}>
            {labels[value]}
          </span>
        );
      })}
    </>
  ),
);

const HiddenTitle = React.memo<{ title: string }>(({ title }) => (
  <span className={classes.label}>{title}</span>
));

const RatingsInput: React.ForwardRefRenderFunction<
  IRatingsInputImperativeActions,
  IRatingsInputProps
> = (props, ref) => {
  const {
    id,
    onChange = () => {},
    onRatingChange = () => {},
    onClick = () => {},
    onDblClick = () => {},
    onMouseEnter = () => {},
    onMouseLeave = () => {},
    iconMouseIn = () => {},
    onFocus = () => {},
    onBlur = () => {},
    labels,
    showLabels,
    showTitle,
    title = '',
    svgString,
    labelPosition,
    required,
    value,
    isDisabled,
    shouldShowValidityIndication,
    isValid,
    validateValueAndShowIndication = () => {},
  } = props;

  const [hoveredItemIndex, setHoveredItemIndex] = React.useState<
    number | undefined
  >(undefined);

  const itemRefs = React.useRef<Array<IRatingsInputImperativeActions | null>>(
    [],
  );

  React.useImperativeHandle(ref, () => {
    const index = (value || 1) - 1;
    return {
      focus() {
        itemRefs.current[index]?.focus();
      },
      blur() {
        itemRefs.current[index]?.blur();
      },
      setCustomValidity: (message: string) => {
        itemRefs.current[index]?.setCustomValidity(message);
      },
    };
  });

  const onIconBlur = () => {
    if (isDisabled) {
      return;
    }
    setHoveredItemIndex(undefined);
  };

  const onIconHover = (
    event: React.MouseEvent,
    icon: { value: number; label: string },
  ) => {
    if (isDisabled) {
      return;
    }

    setHoveredItemIndex(icon.value);
    iconMouseIn({ ...icon, type: 'iconMouseIn' });
  };

  const getCurrentText = React.useCallback(() => {
    if (hoveredItemIndex && showLabels) {
      return labels[hoveredItemIndex];
    } else if (value && showLabels) {
      return labels[value];
    }

    return (showTitle && title) || '';
  }, [hoveredItemIndex, labels, value, showLabels, showTitle, title]);

  const shouldDisplayError =
    shouldShowValidityIndication && !hoveredItemIndex && !isValid;

  const styleStates = {
    labelPosition,
    disabled: isDisabled,
    error: shouldDisplayError,
  };
  const _onChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    const intValue = parseInt(event.target.value, 10);
    const isValueUnchanged = value === intValue;
    if (isDisabled || isValueUnchanged) {
      return;
    }
    onRatingChange(event);
    onChange?.(event);
    validateValueAndShowIndication({ value: intValue });
  };

  return (
    <div
      className={st(classes.root, styleStates)}
      role="radiogroup"
      id={id}
      onClick={onClick}
      onDoubleClick={onDblClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <span className={classes.labelsContainer}>
        {showTitle || showLabels ? <Title title={getCurrentText()} /> : null}
        {showLabels ? <HiddenLabels labels={labels} /> : null}
        {showTitle ? <HiddenTitle title={title} /> : null}
      </span>
      <span
        className={classes.icons}
        aria-label={value ? `${value} out of 5` : undefined}
      >
        {fiveItems.map(itemValue => (
          <RatingsInputItem
            key={itemValue}
            className={classes.item}
            name={id}
            value={itemValue}
            ratingInputValue={value}
            hovered={hoveredItemIndex}
            onChange={_onChange}
            required={required}
            ariaLabel={!value ? labels[itemValue] : undefined}
            onMouseEnter={event =>
              onIconHover(event, { value: itemValue, label: labels[itemValue] })
            }
            onMouseLeave={onIconBlur}
            svgString={svgString}
            ref={el => (itemRefs.current[itemValue - 1] = el)}
            error={shouldDisplayError}
          />
        ))}
      </span>
    </div>
  );
};

export default React.forwardRef(RatingsInput);
